import React from "react";
import { useDropshippingText } from "../i18n";
import QtyStepper from "./QtyStepper";
import { money, variantLabel, nextTierHint } from "./wholesaleCart";
import AddressLocationSelector from "../../pageInside/addressLocation/AddressLocationSelector";
import { toLocationValue, fromLocationValue } from "../../pageInside/addressLocation/addressLocationData";

const DELIVERY_LOCATION_FIELDS = { country: "country", state: "state", city: "city", postalCode: "postalCode" };

// Addresses the Seller already saved (User.address) mapped to the delivery-address shape the backend validates.
export const toDeliveryAddress = (a = {}) => ({
  label: a.shopName || "", name: [a.firstName, a.lastName].filter(Boolean).join(" "), line1: a.Address1 || "", line2: a.Address2 || "",
  city: a.city || "", state: a.State || "", postalCode: a.postalCode || "", country: a.country || "", phone: a.tel || "",
});
const EMPTY_ADDRESS = { name: "", line1: "", line2: "", city: "", state: "", postalCode: "", country: "", phone: "" };
export const addressComplete = (a) => Boolean(a && a.line1 && a.city && a.country);

const Fee = ({ fee, ct, currency }) => {
  if (!fee.known) return <span>{ct("feeToConfirm")}</span>;
  if (fee.free) return <span>{ct("feeFreeLabel")}</span>;
  return <span>{money(fee.amount, currency)}{fee.type === "per_unit" ? ` ${ct("perUnit")}` : ""}</span>;
};

// The purchase side of a Provider product: (1) what to do with the purchased units -- an EXPLICIT choice among the methods the
// Provider enabled --, (2) quantities/variants, (3) the address when shipping, (4) the server's price + fees, (5) review + confirm.
// Nothing here computes a price: every number comes from the server quote.
export default function PurchaseCard({ product, cart, savedAddresses = [], reviewing, setReviewing, onConfirm, errorText }) {
  const ct = useDropshippingText(null, "dropshippingCommercial");
  const { quote, quoteError, totalUnits, delivery } = cart;
  const terms = product.terms?.purchase || {};
  const [addrMode, setAddrMode] = React.useState(savedAddresses.length ? "saved" : "new");
  const [savedIndex, setSavedIndex] = React.useState("");
  const [draft, setDraft] = React.useState(EMPTY_ADDRESS);
  const options = [
    product.actions?.buyAndStore && { key: "provider_storage", label: ct("optStore") },
    product.actions?.buyAndShip && { key: "provider_ships", label: ct("optShip") },
    product.wholesaleEnabled && product.sellerPickupEnabled && { key: "seller_pickup", label: ct("optPickup") },
    product.wholesaleEnabled && product.localDeliveryEnabled && { key: "local_delivery", label: ct("optLocal") },
  ].filter(Boolean);
  const needsAddress = delivery === "provider_ships" || delivery === "local_delivery";
  const minimum = delivery === "provider_storage" ? terms.storeAtProvider?.minimum : delivery === "provider_ships" ? terms.shipToAddress?.minimum : Math.max(1, Number(product.minimumOrderQuantity) || 1);
  const stock = Number(product.stock) || 0;
  const hint = nextTierHint(product, totalUnits);
  const currency = product.currency || "USD";

  const chosenAddress = addrMode === "saved" ? (savedIndex === "" ? null : toDeliveryAddress(savedAddresses[Number(savedIndex)])) : draft;
  React.useEffect(() => { cart.setAddress(needsAddress && addressComplete(chosenAddress) ? chosenAddress : null); }, [needsAddress, addrMode, savedIndex, draft]); // eslint-disable-line react-hooks/exhaustive-deps
  const canReview = Boolean(quote && !quoteError && delivery && totalUnits >= (minimum || 1) && (!needsAddress || addressComplete(chosenAddress)));
  const field = (key, label, extra = {}) => (
    <label className="pc-field"><span>{label}</span><input value={draft[key]} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} {...extra} /></label>
  );

  return (
    <div className="pc" data-testid="purchase-card">
      <h3 className="pc__title">{ct("buyProducts")}</h3>
      <fieldset className="pc__step">
        <legend>{ct("stepTitle")}</legend>
        {options.length === 0 && <p className="ppd__hint">{ct("notEnabled")}</p>}
        {options.map((o) => (
          <label key={o.key} className={`pc__opt${delivery === o.key ? " is-on" : ""}`}>
            <input type="radio" name="delivery" value={o.key} checked={delivery === o.key} onChange={() => { cart.setDelivery(o.key); setReviewing(false); cart.resetAttempt(); }} />
            <span>{o.label}</span>
          </label>
        ))}
        {delivery === "provider_storage" && (
          <div className="pc__conditions" data-testid="store-conditions">
            <strong>{ct("storeConditions")}</strong>
            <ul>
              <li>{ct("feeStorage")}: <Fee fee={terms.storeAtProvider?.storage || {}} ct={ct} currency={currency} /></li>
              <li>{ct("feePurchasedLogistics")}: {terms.storeAtProvider?.logistics?.offered === false ? <span>—</span> : <Fee fee={terms.storeAtProvider?.logistics || {}} ct={ct} currency={currency} />}</li>
            </ul>
            <p>{ct("storeOwnNote")}</p>
          </div>
        )}
        {delivery === "provider_ships" && <div className="pc__conditions"><ul><li>{ct("feeShipping")}: <Fee fee={terms.shipToAddress?.shipping || {}} ct={ct} currency={currency} /></li></ul><p>{ct("shipNote")}</p></div>}
      </fieldset>

      {delivery && (<>
        <div className="pc__qty">
          <strong>{ct("selectedQty")}</strong>
          {product.variants?.length > 0 ? (
            <ul className="ppd__variants">{product.variants.map((v, i) => (
              <li key={i}><span>{variantLabel(v.filter) || `#${i + 1}`}<small>{v.quantity}</small></span>
                <QtyStepper label={variantLabel(v.filter)} value={cart.qty[i] || 0} max={Number(v.quantity) || 0} onChange={(n) => { cart.setQ(i, n, Number(v.quantity) || 0); setReviewing(false); }} /></li>))}</ul>
          ) : <QtyStepper label={ct("selectedQty")} value={cart.qty[""] || 0} max={stock} onChange={(n) => { cart.setQ("", n, stock); setReviewing(false); }} />}
          <p className="ppd__hint" role="status">{ct("minQty")}: <strong>{minimum}</strong> · {ct("selectedQty")}: <strong>{totalUnits}</strong></p>
          {hint && totalUnits >= 1 && <p className="ppd__hint ppd__hint--tier">{ct("unitPrice")}: {money(hint.unit, currency)} ≥ {hint.minQuantity}</p>}
        </div>

        {needsAddress && (
          <fieldset className="pc__address">
            <legend>{ct("addrPick")}</legend>
            {savedAddresses.length > 0 && (
              <select aria-label={ct("addrPick")} value={addrMode === "saved" ? savedIndex : "__new"} onChange={(e) => { if (e.target.value === "__new") setAddrMode("new"); else { setAddrMode("saved"); setSavedIndex(e.target.value); } setReviewing(false); }}>
                <option value="">{ct("addrChoose")}</option>
                {savedAddresses.map((a, i) => <option key={i} value={i}>{[a.shopName, a.Address1, a.city, a.country].filter(Boolean).join(" — ")}</option>)}
                <option value="__new">{ct("addrOther")}</option>
              </select>
            )}
            {addrMode === "new" && (
              <div className="pc__addrform">
                {field("name", ct("addrName"))}{field("line1", ct("addrLine1"), { required: true })}{field("line2", ct("addrLine2"))}
                <AddressLocationSelector
                  idPrefix="pc-delivery"
                  value={toLocationValue(draft, DELIVERY_LOCATION_FIELDS)}
                  onChange={(location) => { setDraft((d) => fromLocationValue(d, location, DELIVERY_LOCATION_FIELDS)); setReviewing(false); }}
                  labels={{ country: ct("addrCountry"), city: ct("addrCity") }}
                  classNames={{ field: "pc-field" }}
                />
                {field("phone", ct("addrPhone"))}
              </div>
            )}
            {!addressComplete(chosenAddress) && <small className="ppd__hint">{ct("addrRequired")}</small>}
          </fieldset>
        )}

        <div aria-live="polite" className="pc__summary">
          {quoteError && <div className="alert alert-warning" role="alert">{errorText(quoteError)}</div>}
          {quote && !quoteError && (<>
            <ul className="wb__lines">{quote.lines.map((l, i) => <li key={i}><span>{l.quantity} × {money(l.unitPrice, quote.currency)}{l.variant ? ` (${variantLabel(l.variant)})` : ""}</span><strong>{money(l.lineTotal, quote.currency)}</strong></li>)}</ul>
            <p className="pc__row"><span>{ct("sumProducts")}</span><strong>{money(quote.total, quote.currency)}</strong></p>
            {(quote.fees || []).map((f) => (
              <p className="pc__row" key={f.key}><span>{f.key === "storage" ? ct("sumStorage") : f.key === "shipping" ? ct("sumShipping") : ct("sumLogistics")}</span>
                <span>{f.chargedNow ? (f.known ? (f.free ? ct("feeFreeLabel") : money(f.total, quote.currency)) : ct("feeToConfirm")) : (f.offered === false ? "—" : <Fee fee={f} ct={ct} currency={quote.currency} />)}</span></p>
            ))}
            <p className="wb__total"><span>{ct("sumGrand")}</span><strong>{money(quote.grandTotal ?? quote.total, quote.currency)}</strong></p>
            {quote.grandTotalComplete === false && <p className="ppd__hint">{ct("totalIncomplete")}</p>}
          </>)}
        </div>

        {!reviewing
          ? <button type="button" className="dsia-submit-btn" disabled={!canReview} onClick={() => setReviewing(true)}>{ct("reviewSummary")}</button>
          : <>
              <label className="wb__note">{ct("reviewSummary")}<textarea maxLength={1000} value={cart.note} onChange={(e) => cart.setNote(e.target.value)} /></label>
              <p className="ppd__hint">{ct("providerConfirms")}</p>
              <button type="button" className="dsia-submit-btn" disabled={cart.busy} onClick={onConfirm}>{ct("confirmPurchase")}</button>
              <button type="button" className="dsia-cancel-btn" onClick={() => setReviewing(false)}>←</button>
            </>}
      </>)}
    </div>
  );
}
